"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { Check, Plus, Crown } from "lucide-react";

const GUEST_LIST = [
  { name: "Carlos Reyes", tier: "VIP", in: true, time: "10:48 PM", promoter: "Sofia" },
  { name: "Daniela López", tier: "GA", in: true, time: "10:51 PM", promoter: "Direct" },
  { name: "Marco Aurelio", tier: "GA+1", in: false, time: null, promoter: "Sofia" },
  { name: "Sara Williams", tier: "VIP", in: true, time: "10:54 PM", promoter: "Direct" },
];

export default function QrDoorGuestListScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-[#0F0B1F] text-white overflow-hidden">
      <div
        className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(124,58,237,0.18) 0%, transparent 65%)" }}
      />

      <div className="relative max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#F472B6" }}>
            {t("Guest list at the door", "Guest list en la puerta")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02]">
            {t("Walk-ups too. ", "Walk-ups también. ")}
            <span className="text-white/30">{t("Not just tickets.", "No solo tickets.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-white/55 leading-relaxed mt-6">
            {t(
              "Door host can sell a walk-up a ticket and check in guest-list names right from the door screen. Pick the tier, done. They're on tonight's list with everyone else.",
              "El host de puerta le vende el ticket a un walk-up y hace check-in de la guest list desde la misma pantalla de puerta. Eliges el tier, listo. Quedan en la lista de la noche con todos los demás."
            )}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-[24px] p-6 md:p-7 max-w-[760px] mx-auto"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <div className="text-[10px] font-black tracking-widest uppercase text-white/45">
              {t("Tonight's list", "Lista de la noche")}
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: "#F472B6" }} />
              <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#F472B6" }}>
                Live
              </span>
            </div>
          </div>

          {/* Guest list */}
          <div className="space-y-2 mb-4">
            {GUEST_LIST.map((g, i) => (
              <motion.div
                key={g.name}
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.45, delay: 0.15 + 0.1 * i }}
                className="bg-white/[0.03] rounded-[14px] p-3.5 border border-white/5 flex items-center gap-3"
              >
                {/* Status check */}
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                  style={{
                    backgroundColor: g.in ? "rgba(46,204,113,0.20)" : "rgba(255,255,255,0.04)",
                    border: g.in ? "none" : "1px dashed rgba(255,255,255,0.15)",
                  }}
                >
                  {g.in ? (
                    <Check className="w-4 h-4" style={{ color: "#2ECC71" }} strokeWidth={3} />
                  ) : (
                    <span className="text-[10px] font-bold text-white/35">—</span>
                  )}
                </div>

                {/* Name + tier */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="text-[13px] font-black text-white truncate">{g.name}</div>
                    <div
                      className="text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                      style={{
                        backgroundColor: g.tier.startsWith("VIP")
                          ? "rgba(244,114,182,0.18)"
                          : "rgba(255,255,255,0.06)",
                        color: g.tier.startsWith("VIP") ? "#F472B6" : "rgba(255,255,255,0.55)",
                      }}
                    >
                      {g.tier.startsWith("VIP") && <Crown className="w-2 h-2 inline mr-0.5" />}
                      {g.tier}
                    </div>
                  </div>
                  <div className="text-[10px] text-white/45 mt-0.5">
                    {g.in ? (
                      <>
                        {t("In at", "Adentro a las")} <span className="text-white/65 tabular-nums">{g.time}</span>
                        {" · "}
                        <span>{g.promoter === "Direct" ? t("Direct sale", "Venta directa") : `via ${g.promoter}`}</span>
                      </>
                    ) : (
                      <span style={{ color: "#FB923C" }}>{t("Walk-up · sold at door", "Walk-up · vendido en puerta")}</span>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Add walk-up button */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="rounded-[14px] p-3.5 flex items-center justify-between"
            style={{
              background: "linear-gradient(135deg, #7C3AED 0%, #F472B6 100%)",
            }}
          >
            <div className="flex items-center gap-2.5">
              <Plus className="w-4 h-4 text-white" strokeWidth={3} />
              <span className="text-[12px] font-bold text-white">
                {t("Sell a walk-up ticket", "Vender ticket a walk-up")}
              </span>
            </div>
            <div className="text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full bg-white/20 text-white">
              {t("In seconds", "En segundos")}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
